/**
 * ExtFieldValueInput — wspólny edytor wartości pojedynczego pola dodatkowego (ext-field).
 *
 * INPUT-ONLY: renderuje WYŁĄCZNIE samą kontrolkę dobraną po `field.fieldType` —
 * etykieta / gwiazdka „wymagane" / tekst błędu zostają w ramce po stronie callsite'a
 * (`<Field>` / `<FieldWrap>`). Komponent ujednolica logikę typ→kontrolka, dotąd
 * skopiowaną w 11 miejscach (T12.S.B.5). Zachowanie 1:1 ze „złotym" wzorcem
 * edytora wartości pola dodatkowego.
 *
 * Błąd: gdy `error` jest obecny → ustawia `aria-invalid` na kontrolce i przekazuje
 * `aria-describedby`; TEKSTU błędu NIE renderuje (zostaje w ramce). Dlatego do
 * bazowych `<Input>`/`<Select>`/`<Textarea>` NIE przekazujemy propa `error`
 * (renderowałby tekst) — `aria-invalid` leci przez spread i nadpisuje wartość bazową.
 *
 * Etap 4b — select/multiselect ze słownikiem: gdy `field.dictionaryCode` jest
 * niepuste, opcje pochodzą z `useDictionary(dictionaryCode)` (pozycje wybieralne,
 * czyli aktywne i nieukryte) zamiast z `field.options` — wartość opcji to KOD
 * pozycji, etykieta formatowana wg `dictionary.displayMode` (patrz
 * `formatDictionaryOptionLabel`). Brak `dictionaryCode` → zachowanie bez zmian
 * (opcje inline z `field.options`). Aktualnie zapisana wartość jest zawsze
 * renderowana (nawet gdy jej pozycja stała się nieaktywna/ukryta) — inaczej
 * istniejące dane „znikałyby" z selecta.
 *
 * Etap 10.3 (ADR-0066 §11), Faza 2b (ADR-0068) — słownik zewnętrzny z wiązaniami: gdy
 * `field.dictionaryParamBindings` jest NIEPUSTE (mapa nazwa-parametru-źródła →
 * wiązanie: tagowane lub legacy string), opcje pochodzą z `useDictionaryOptions`
 * (`POST .../options` z RAW wiązaniami pola w `bindings` + aktualnymi wartościami
 * sąsiadów w `recordValues` — rozwiązywanie per parametr, w tym `context.*`, robi
 * SERWER) zamiast z `useDictionary` (`GET .../[code]`). Fetch odpalany PRZY OTWARCIU
 * kontrolki, nie na mount — `<select>` przez `onFocus`/`onBlur` (`enabled`
 * false→true→false), `MultiSelect` przez jego własny `onOpenChange` (śledzi otwarcie
 * wewnętrznie — bąbelkujący focus/blur okazał się niewiarygodny, patrz komentarz przy
 * gałęzi `multiselect`). `staleTime:0` w hooku gwarantuje świeży odczyt nawet gdy
 * wartości sąsiadów się nie zmieniły między otwarciami. Pole BEZ wiązań LUB bez
 * rozwiązywalnego `dictionaryCode` → zachowanie bez zmian (`useDictionary`, zero
 * regresji; `shouldArmOptionsFetch` gate).
 *
 * Etap 10.5 (ADR-0066 §11) — błąd materializacji słownika zewnętrznego zwrócony
 * przez `useDictionary`/`useDictionaryOptions` (`error`, patrz `DictionaryQueryError`)
 * jest renderowany jako czytelny komunikat (`mapDictionaryQueryError`): dla
 * 'select' przez prop `error` bazowego `<Select>` (już renderuje tekst pod
 * kontrolką, `role="alert"`); dla 'multiselect' (brak wsparcia `error` w
 * `<MultiSelect>`) przez dodatkowy `<p role="alert">` dopisany po kontrolce.
 * Dropdown zostaje pusty (bez crasha) — user widzi PRZYCZYNĘ, nie tylko efekt.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { EmbeddedTableColumnAlign } from '@saas/shared';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import { MultiSelect } from '@/components/MultiSelect';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Textarea } from '@/components/Textarea';
import { OpenRecordCardButton } from '@/components/ui/OpenRecordCardButton';
import { RecordTablePicker } from '@/components/ui/RecordTablePicker';
import { useRelationPickerRecordActions } from '@/components/ui/RelationTablePickerButton';
import {
  type DictionaryDetail,
  type DictionaryItem,
  type DictionaryParamBindingsMap,
  formatDictionaryOptionLabel,
  hasParamBindings,
  mapDictionaryQueryError,
  useDictionary,
  useDictionaryOptions,
} from '@/hooks/useDictionary';
import { api } from '@/lib/api';
import { listControlValueAlignClass, multiSelectValueAlignClass } from '@/lib/embedded-table/cells';
import { getSystemEntityAdapter } from '@/lib/system-entity-adapters';

// Re-eksport dla modułu silnika formatek (E4 §2.3) — konsument importuje typ
// wiązań razem z komponentem, bez sięgania do wewnętrznego hooka.
export type { DictionaryParamBindingsMap };

/**
 * Minimalny wspólny kształt pola wymagany przez edytor. Strukturalnie spełniany
 * przez `ExtFieldDef` (system-modules) oraz lokalne `SystemModuleField` z callsitów
 * — przekazywane bez castów (duck-typing).
 */
export interface ExtFieldValueInputField {
  slug: string;
  name: string;
  fieldType: string;
  isRequired: boolean;
  description: string | null;
  options: Array<{ value: string; label: string }> | null;
  /** Kod słownika (`dictionaries.code`) — gdy obecny, select/multiselect czerpie
   *  opcje ze słownika zamiast z `options`. Opcjonalne — callsite'y bez tej
   *  funkcji (jeszcze) nie muszą go przekazywać (duck-typing, brak = brak zmiany
   *  zachowania). */
  dictionaryCode?: string | null;
  /** Nadpisanie sposobu wyświetlania pozycji słownika dla TEGO pola (Etap 1c) —
   *  `null`/`undefined` = użyj `dictionary.displayMode` (fallback). */
  dictionaryDisplayMode?: 'label' | 'code' | 'code_label' | null;
  /**
   * Wiązania parametrów słownika zewnętrznego (Etap 10.2/10.3, ADR-0066 §11; Faza 2b
   * ADR-0068 — tagowane lub legacy string = record_field). Niepuste → opcje pobierane
   * przez `useDictionaryOptions` (RAW `bindings` + `recordValues` — serwer rozwiązuje
   * wartości per parametr) zamiast przez `useDictionary`. `null`/`undefined`/pusta
   * mapa = brak wiązań, zachowanie bez zmian.
   */
  dictionaryParamBindings?: DictionaryParamBindingsMap | null;
  /**
   * Slug modułu systemowego (kartoteki) będącego celem relacji — F.REF v1
   * (nowy kontrakt `GET /system-modules/{slug}/fields`), meaningful WYŁĄCZNIE
   * dla `fieldType === 'reference'`. Rozwiązywany przez `getSystemEntityAdapter`
   * (`lib/system-entity-adapters.ts`) — dziś w pełni obsługiwane targety to
   * `contractors` (jedyny adapter płaski z `coreFieldMap`) i `documents` (F20 —
   * JEDEN generyczny token dla dowolnej z 7 klas dokumentów, klasa wybierana w
   * runtime wewnątrz `RecordTablePicker`, adapter z `classDimension`), oba
   * wymagane przez `RecordTablePicker`. `null`/`undefined` = cel nieskonfigurowany
   * — renderer pokazuje komunikat „skonfiguruj kartotekę" (mirror
   * `RelationFieldType`'s `NotConfigured`) zamiast pickera.
   */
  relationSystemModuleSlug?: string | null;
  /**
   * Czy pole odwołania pozwala wybrać wiele rekordów. F.REF v1: renderer
   * ZAWSZE renderuje single-select niezależnie od tej flagi (multi-select jest
   * kolejnym krokiem) — trzymana wyłącznie żeby wartość zrobiła pełny
   * roundtrip przez formularz bez utraty informacji. `undefined`/`false` =
   * pojedynczy wybór (dzisiejsze, jedyne wspierane zachowanie).
   */
  relationMultiple?: boolean;
}

export interface ExtFieldValueInputProps {
  field: ExtFieldValueInputField;
  value: unknown;
  onChange: (v: unknown) => void;
  disabled?: boolean;
  /** Obecność błędu → `aria-invalid` na kontrolce. Tekst NIE jest renderowany. */
  error?: string;
  /** Jak `error`, ale bez treści komunikatu (żyje np. w toaście) — czerwona ramka na kontrolkach varchar/text/integer/numeric/date/timestamp/file/reference. */
  invalid?: boolean;
  /** Id elementu z komunikatem błędu w ramce callsite'a — trafia na kontrolkę. */
  'aria-describedby'?: string;
  /** Etykieta a11y kontrolki (finding [LOW] przeglądu frontendu) — callsite'y bez
   *  zewnętrznego `<label htmlFor>` (np. komórka gridu edytora pozycji) przekazują
   *  ją zamiast tego. Opcjonalne, addytywne — brak = zero zmiany zachowania. Nie
   *  wspierane przez branch `multiselect` (`<MultiSelect>` nie przyjmuje tego propa
   *  dziś — ma własny `label` o innej semantyce, widocznej etykiecie). */
  'aria-label'?: string;
  /** `data-testid` na samej kontrolce. */
  'data-testid'?: string;
  /** `id` kontrolki — pozwala powiązać zewnętrzną etykietę przez `htmlFor`. */
  id?: string;
  /**
   * Wartości pozostałych pól TEGO SAMEGO rekordu (slug → wartość) — Etap 10.3
   * (ADR-0066 §11). Jedyne źródło dla `dictionaryParamBindings` (parametry
   * `record_field` słownika zewnętrznego): callsite przekazuje mapę wartości,
   * którą i tak trzyma (stan `extValues`/`formValues` rekordu). Brak → pola
   * param-bound dostają pusty `recordParams` (backend zwróci
   * `EXTERNAL_PARAM_MISSING` dla wymaganych parametrów, opcje = pusta lista).
   */
  recordValues?: Record<string, unknown>;
  /**
   * Limit znaków kontrolki tekstowej (E4 §2.3) — aplikowany TYLKO w branchach
   * `varchar`/`email`/`url` (natywny `maxLength` na `<Input>`) oraz `text`
   * (`<Textarea>`). Pozostałe typy ignorują. Brak = bez limitu (zachowanie
   * dotychczasowe).
   */
  maxLength?: number;
  /**
   * Liczba wierszy `<Textarea>` dla typu `text` (E4 §2.3). Brak = 3 (dotychczasowa
   * wartość zaszyta na sztywno). Pozostałe typy ignorują.
   */
  rows?: number;
  /**
   * Jawne wyrównanie WARTOŚCI kontrolki list — dotyczy WYŁĄCZNIE `select` i `multiselect`
   * (komórka tabeli z układem kolumn; wyrównanie klasą na komórce nie dosięga tekstu w ich
   * przycisku, więc idzie przez `valueClassName` kontrolek). Pola tekstowe/liczbowe wyrównuje
   * wołający (`text-*` na `<input>`). Brak = wygląd domyślny (do lewej), bez zmian.
   */
  valueAlign?: EmbeddedTableColumnAlign;
}

export function ExtFieldValueInput({
  field,
  value,
  onChange,
  disabled,
  error,
  invalid,
  'aria-describedby': describedBy,
  'aria-label': ariaLabel,
  'data-testid': testId,
  id,
  recordValues,
  maxLength,
  rows,
  valueAlign,
}: ExtFieldValueInputProps) {
  const { t } = useTranslation();

  // Hooki wywoływane bezwarunkowo (reguła React) — `useDictionary` samo gates
  // przez `enabled: !!code`, więc pusty string dla pól bez dictionaryCode nie
  // odpala zapytania sieciowego.
  const dictionaryCode = field.dictionaryCode ?? '';

  // Etap 10.3 — wybór trasy: pole z niepustym `dictionaryParamBindings` (parametry
  // `record_field`) pobiera opcje przez `useDictionaryOptions` (POST .../options,
  // fetch-on-open, zero cache); pole bez wiązań zostaje na `useDictionary` (GET
  // .../[code], zero regresji). `code=''` na nieużywanej ścieżce wyłącza jej fetch
  // (gate wewnątrz obu hooków), więc nigdy nie płacimy za dwa równoległe zapytania.
  const paramBindings = field.dictionaryParamBindings ?? null;
  const fieldHasParamBindings = hasParamBindings(paramBindings);
  // Finding B (review): a field with bindings but no target dictionary has
  // nothing to fetch — gate the fetch-arm machinery (onFocus/onBlur on
  // <select>, onOpenChange on MultiSelect) on BOTH, not on bindings alone.
  const shouldArmOptionsFetch = fieldHasParamBindings && !!field.dictionaryCode;

  const {
    dictionary: detailDictionary,
    activeItems: detailActiveItems,
    error: detailError,
  } = useDictionary(fieldHasParamBindings ? '' : dictionaryCode);

  // Fetch-on-open: `optionsFetchArmed` przełącza `enabled` false→true na `onFocus`
  // kontrolki i z powrotem na `onBlur` — dzięki temu KAŻDE (re)otwarcie wyzwala
  // świeże zapytanie (nawet gdy `recordParams` się nie zmieniły, `staleTime:0` w
  // hooku wymusza refetch po ponownym `enabled:true`), a nie tylko pierwsze.
  const [optionsFetchArmed, setOptionsFetchArmed] = useState(false);
  const armOptionsFetch = () => setOptionsFetchArmed(true);
  const disarmOptionsFetch = () => setOptionsFetchArmed(false);

  // Faza 2b (ADR-0068): RAW wiązania pola + wartości sąsiadów idą do serwera —
  // rozwiązywanie wartości per parametr (w tym `context.*`) robi endpoint options,
  // nie klient (dawny `buildDictionaryRecordParams` usunięty).
  const {
    dictionary: optionsDictionary,
    activeItems: optionsActiveItems,
    error: optionsError,
  } = useDictionaryOptions(fieldHasParamBindings ? dictionaryCode : '', {
    bindings: paramBindings,
    recordValues: recordValues ?? {},
    enabled: fieldHasParamBindings && optionsFetchArmed,
  });

  const dictionary = fieldHasParamBindings ? optionsDictionary : detailDictionary;
  const dictionaryActiveItems = fieldHasParamBindings ? optionsActiveItems : detailActiveItems;
  // Etap 10.5 — błąd z WHICHEVER ścieżka jest aktywna (tak jak `dictionary`/
  // `dictionaryActiveItems` powyżej): param-bound fields mogą dostać
  // EXTERNAL_PARAM_MISSING/INVALID z `useDictionaryOptions`; statyczne pola
  // (bez wiązań, ale sourceKind='external' z parametrami context.*) mogą
  // dostać EXTERNAL_SOURCE_UNAVAILABLE z `useDictionary`.
  const dictionaryError = fieldHasParamBindings ? optionsError : detailError;
  const dictionaryErrorMessage = mapDictionaryQueryError(dictionaryError, t);

  // Wspólne propsy przekazywane na bazową kontrolkę. `aria-invalid` przez spread
  // nadpisuje wartość ustawianą przez bazowy komponent (który czyta `error`,
  // a tego celowo nie podajemy, by nie renderować tekstu błędu).
  const passthrough = {
    id,
    disabled,
    'data-testid': testId,
    'aria-describedby': describedBy,
    'aria-label': ariaLabel,
    'aria-invalid': error || invalid ? (true as const) : undefined,
  };

  switch (field.fieldType) {
    case 'varchar':
    case 'email':
    case 'url':
      return (
        <Input
          type={field.fieldType === 'varchar' ? 'text' : field.fieldType}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          maxLength={maxLength}
          invalid={invalid}
          {...passthrough}
        />
      );

    case 'text':
      return (
        <Textarea
          rows={rows ?? 3}
          maxLength={maxLength}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          invalid={invalid}
          {...passthrough}
        />
      );

    case 'integer':
      return (
        <Input
          type="number"
          step={1}
          value={value !== null && value !== undefined ? String(value) : ''}
          onChange={(e) => {
            const v = e.target.value;
            onChange(v === '' ? null : Number.parseInt(v, 10));
          }}
          invalid={invalid}
          {...passthrough}
        />
      );

    case 'numeric':
      return (
        <Input
          type="number"
          step={0.0001}
          value={value !== null && value !== undefined ? String(value) : ''}
          onChange={(e) => {
            const v = e.target.value;
            onChange(v === '' ? null : Number.parseFloat(v));
          }}
          invalid={invalid}
          {...passthrough}
        />
      );

    case 'date':
      return (
        <Input
          type="date"
          value={value ? String(value).slice(0, 10) : ''}
          onChange={(e) => onChange(e.target.value || null)}
          invalid={invalid}
          {...passthrough}
        />
      );

    case 'timestamp':
      return (
        <Input
          type="datetime-local"
          value={value ? String(value).slice(0, 16) : ''}
          onChange={(e) => onChange(e.target.value || null)}
          invalid={invalid}
          {...passthrough}
        />
      );

    case 'boolean':
      return (
        <Checkbox
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
          {...passthrough}
        />
      );

    case 'select': {
      const options = resolveSelectOptions(field, dictionary, dictionaryActiveItems);
      // Wartość zapisana wcześniej, której pozycja słownikowa zniknęła z listy
      // wybieralnych (deaktywowana/ukryta po fakcie) — pokaż raw kod, żeby
      // istniejące dane nie „znikały" z pola.
      const currentValue = (value as string) ?? '';
      // WF NEW 2026-08-14 — globalnie: selecty słownikowe z wyszukiwarką (`SearchableSelect`
      // zamiast Radix `Dropdown`). Pole nie-wymagane dostaje `allowClear` (X) zamiast pustej
      // opcji „—"; pole wymagane — bez czyszczenia. Panel portaluje (fixed), więc nie przycina
      // się w modalach (§4.12). Fetch-on-open (pola param-bound) idzie przez `onOpenChange`.
      const selectOptions = currentValue ? ensureOptionPresent(options, currentValue) : options;
      const selectEl = (
        <SearchableSelect
          options={selectOptions}
          placeholder={t('common.selectPlaceholder')}
          value={currentValue || null}
          onChange={(v) => onChange(v)}
          disabled={disabled}
          allowClear={!field.isRequired}
          valueClassName={listControlValueAlignClass(valueAlign)}
          data-testid={testId}
          onOpenChange={
            shouldArmOptionsFetch
              ? (open) => (open ? armOptionsFetch() : disarmOptionsFetch())
              : undefined
          }
        />
      );
      // Etap 10.5 — błąd materializacji słownika zewnętrznego: dawny `<Select>`
      // renderował tekst pod kontrolką przez prop `error`; `Dropdown` go nie ma —
      // dokładamy `<p role="alert">` po kontrolce (mirror gałęzi `multiselect`).
      if (!dictionaryErrorMessage) return selectEl;
      return (
        <>
          {selectEl}
          <p className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
            {dictionaryErrorMessage}
          </p>
        </>
      );
    }

    case 'multiselect': {
      const options = resolveSelectOptions(field, dictionary, dictionaryActiveItems);
      const current = Array.isArray(value) ? (value as string[]) : [];
      // Zachowaj zapisane kody, których pozycja słownikowa mogła zniknąć z listy
      // wybieralnych — bez tego MultiSelect renderowałby chip bez etykiety.
      const optionsWithStale = current.reduce(
        (acc, code) => ensureOptionPresent(acc, code),
        options,
      );
      // Etap 10.5 — `<MultiSelect>` nie ma wsparcia dla propa `error` (w
      // odróżnieniu od `<Select>` powyżej) — bez błędu zwracamy dokładnie ten
      // sam pojedynczy element co przed tym etapem; z błędem dokładamy
      // `<p role="alert">` po kontrolce (Fragment).
      const multiSelectEl = (
        <MultiSelect
          options={optionsWithStale}
          value={current}
          onChange={(v) => onChange(v)}
          disabled={disabled}
          valueClassName={multiSelectValueAlignClass(valueAlign)}
          data-testid={testId}
          // Etap 10.3 fetch-on-open (review fix) — `MultiSelect` śledzi swój
          // stan otwarcia wewnętrznie, więc uzbrajamy/rozbrajamy przez jego
          // `onOpenChange` zamiast owijać go w `<div onFocus/onBlur>`: ten
          // wcześniejszy wzorzec bąbelkującego focus/blur okazał się
          // niewiarygodny (zamknięcie dropdownu odmontowuje wewnętrzny
          // `<input autoFocus>` search boxa, a blur usuwanego z DOM elementu
          // nie bąbelkuje do przodków w praktyce — wrapper zostawał uzbrojony
          // NA STAŁE po pierwszym otwarciu). Pola bez rozwiązywalnego
          // słownika (Finding B): `undefined` — zero zmiany zachowania
          // `MultiSelect`.
          onOpenChange={shouldArmOptionsFetch ? setOptionsFetchArmed : undefined}
        />
      );
      if (!dictionaryErrorMessage) return multiSelectEl;
      return (
        <>
          {multiSelectEl}
          <p className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
            {dictionaryErrorMessage}
          </p>
        </>
      );
    }

    case 'file':
      // Etap „pole definiowalne typu plik": kolumna PG = TEXT na referencję pliku
      // (klucz/ścieżka MinIO lub id załącznika). Pełny uploader do MinIO /
      // `record_attachments` w runtime u tenanta = OSOBNY późniejszy etap — tu
      // wystarczy prosty input na referencję (tekst/URL), spójny z mapowaniem TEXT.
      return (
        <Input
          type="text"
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          placeholder={t('common.extField.filePlaceholder')}
          maxLength={maxLength}
          invalid={invalid}
          {...passthrough}
        />
      );

    case 'reference':
      return (
        <ReferenceRecordInput
          field={field}
          value={value}
          onChange={onChange}
          disabled={disabled}
          invalid={Boolean(error) || invalid}
          describedBy={describedBy}
          ariaLabel={ariaLabel}
          testId={testId}
          id={id}
        />
      );

    default:
      return (
        <div
          className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300"
          data-testid={testId}
        >
          {t('common.extField.unsupportedType', { type: field.fieldType })}
        </div>
      );
  }
}

/**
 * Buduje listę opcji select/multiselect: gdy pole ma `dictionaryCode`, opcje
 * pochodzą ze słownika (wartość = kod pozycji, etykieta wg `displayMode`);
 * inaczej — niezmieniony fallback na `field.options` inline. Gdy słownik jest
 * jeszcze w trakcie ładowania (lub nie istnieje), zwraca pustą listę zamiast
 * rzucać — brak crasha, po prostu pusty dropdown do czasu odpowiedzi.
 */
function resolveSelectOptions(
  field: ExtFieldValueInputField,
  dictionary: DictionaryDetail | null,
  dictionaryActiveItems: DictionaryItem[],
): Array<{ value: string; label: string }> {
  if (field.dictionaryCode) {
    if (!dictionary) return [];
    return dictionaryActiveItems.map((item) => ({
      value: item.code,
      label: formatDictionaryOptionLabel(
        item,
        field.dictionaryDisplayMode ?? dictionary.displayMode,
      ),
    }));
  }
  return (field.options ?? []).map((o) => ({
    value: String(o.value),
    label: o.label,
  }));
}

/**
 * Gdy zapisana wartość nie występuje wśród opcji (pozycja słownikowa stała się
 * nieaktywna/ukryta PO zapisaniu rekordu) — dokłada opcję z surowym kodem jako
 * etykietą, żeby istniejące dane nie „znikały" z selecta/multiselecta. Pomija
 * pustą wartość (obsługiwana osobno przez placeholder select'a).
 */
function ensureOptionPresent(
  options: Array<{ value: string; label: string }>,
  value: string,
): Array<{ value: string; label: string }> {
  if (!value) return options;
  const lowerValue = value.toLowerCase();
  if (options.some((o) => o.value.toLowerCase() === lowerValue)) return options;
  return [...options, { value, label: value }];
}

interface ReferenceRecordInputProps {
  field: ExtFieldValueInputField;
  value: unknown;
  onChange: (v: unknown) => void;
  disabled?: boolean;
  invalid?: boolean;
  describedBy?: string;
  ariaLabel?: string;
  testId?: string;
  id?: string;
}

/**
 * Kontrolka dla `fieldType === 'reference'` (F.REF v1) — zastępuje dawny surowy
 * `<Input>` na UUID (Etap poprzedzający) pickerem-tabelą (`RecordTablePicker`).
 *
 * Cel relacji (`field.relationSystemModuleSlug`) rozwiązywany przez
 * `getSystemEntityAdapter` (ten sam rejestr, którego używa `RelationFieldType`
 * dla pola „relacja" w Kreatorze modułów) — trzy stany:
 *  - brak sluga → komunikat „skonfiguruj kartotekę" (kreator go nie ustawił),
 *  - slug ustawiony, ale spoza rejestru adapterów → komunikat „nieobsługiwane",
 *  - slug rozwiązany → trigger z etykietą aktualnie wybranego rekordu + przycisk
 *    otwierający `RecordTablePicker` (modal-tabela, single-select v1).
 *
 * Etykieta wybranego rekordu dociągana ze szczegółu kartoteki (`adapter.detailPath`)
 * — kartoteki nie wspierają pobrania „po id" z listy, mirror `RelationPicker`.
 *
 * `companyId` NIE jest przekazywane do pickera (v1: wybór ze wszystkich firm) —
 * ten komponent nie ma dostępu do „firmy edytowanego rekordu" (żaden callsite
 * dziś nie przekazuje takiego kontekstu do `ExtFieldValueInput`); dociąganie go
 * przez globalny `useActiveCompanyScope()` byłoby myląca, niedokładna proxy (aktywna
 * firma z top-bara ≠ firma rekordu), więc świadomie pominięte do czasu realnego
 * potrzebnego kontekstu (patrz raport zadania).
 */
function ReferenceRecordInput({
  field,
  value,
  onChange,
  disabled,
  invalid,
  describedBy,
  ariaLabel,
  testId,
  id,
}: ReferenceRecordInputProps) {
  const { t } = useTranslation();
  const [pickerOpen, setPickerOpen] = useState(false);

  const targetSlug = field.relationSystemModuleSlug ?? null;
  const adapter = getSystemEntityAdapter(targetSlug);
  const recordId = typeof value === 'string' && value !== '' ? value : null;

  const labelQuery = useQuery<string | null>({
    queryKey: ['ext-field-reference-label', adapter?.slug, recordId],
    queryFn: async () => {
      if (!adapter || !recordId) return null;
      try {
        // Adaptery z `resolveByIds` (dziś: WYŁĄCZNIE `documents`) nie mają endpointu
        // „szczegół po id" — rozwiązanie idzie przez batch `?ids=` (jeden element).
        if (adapter.resolveByIds) {
          const res = await api.get<Record<string, unknown>>(adapter.resolveByIds.path([recordId]));
          const rows =
            (res.data[adapter.resolveByIds.responseKey] as Record<string, unknown>[] | undefined) ??
            [];
          const raw = rows[0];
          return raw ? adapter.getLabel(raw) : null;
        }
        const detailPath = adapter.detailPath;
        const detailResponseKey = adapter.detailResponseKey;
        if (!detailPath || !detailResponseKey) return null;
        const res = await api.get<Record<string, unknown>>(detailPath(recordId));
        const raw = res.data[detailResponseKey] as Record<string, unknown> | undefined;
        return raw ? adapter.getLabel(raw) : null;
      } catch {
        return null;
      }
    },
    enabled: !!adapter && !!recordId,
    staleTime: 60_000,
  });

  // Akcje okna wyboru (otwórz/edytuj/dodaj) — wspólny silnik relacji, ten sam co w polu relacji
  // kartotek własnych i przy „Dostawcy" na fakturze. Wołane przed wczesnymi `return` (hooki).
  const { recordActions, modals } = useRelationPickerRecordActions({
    entity: adapter?.slug ?? '',
    closePicker: () => setPickerOpen(false),
    onCreated: (createdId) => onChange(createdId),
  });

  // Kartoteka wskazana, ale nieobsługiwana w runtime (slug spoza mapy adapterów) —
  // mirror `RelationFieldType`'s `NotConfigured` (slug obsługiwany, encja nie).
  if (targetSlug && !adapter) {
    return (
      <div
        id={id}
        data-testid={testId}
        className="rounded-md border border-border-primary bg-bg-secondary px-3 py-2 text-sm text-text-muted"
      >
        {t('common.extField.referencePicker.unsupported', { slug: targetSlug })}
      </div>
    );
  }

  // Pole nie ma skonfigurowanego celu (kreator nie ustawił kartoteki docelowej).
  if (!adapter) {
    return (
      <div
        id={id}
        data-testid={testId}
        className="rounded-md border border-border-primary bg-bg-secondary px-3 py-2 text-sm text-text-muted"
      >
        {t('common.extField.referencePicker.notConfigured')}
      </div>
    );
  }

  const label = recordId ? (labelQuery.data ?? `${recordId.slice(0, 8)}…`) : null;

  return (
    <div id={id} data-testid={testId} className="flex items-center gap-2">
      <span
        className="min-w-0 flex-1 truncate rounded-md border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-primary"
        data-testid={testId ? `${testId}-label` : undefined}
      >
        {label ?? t('common.extField.referencePicker.empty')}
      </span>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => setPickerOpen(true)}
        disabled={disabled}
        aria-describedby={describedBy}
        aria-label={ariaLabel}
        aria-invalid={invalid ? true : undefined}
        data-testid={testId ? `${testId}-trigger` : undefined}
      >
        {recordId
          ? t('common.extField.referencePicker.change')
          : t('common.extField.referencePicker.select')}
      </Button>
      {recordId && !disabled && (
        <IconButton
          variant="ghost"
          size="sm"
          onClick={() => onChange(null)}
          aria-label={t('common.extField.referencePicker.clear')}
          data-testid={testId ? `${testId}-clear` : undefined}
        >
          <X aria-hidden="true" />
        </IconButton>
      )}
      <OpenRecordCardButton
        systemSlug={adapter.slug}
        recordId={recordId}
        testId={testId ? `${testId}-open-card` : undefined}
      />
      <RecordTablePicker
        entity={adapter.slug}
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        value={recordId}
        onChange={(pickedId) => onChange(pickedId)}
        recordActions={recordActions}
        data-testid={testId ? `${testId}-picker` : undefined}
      />
      {modals}
    </div>
  );
}
